/** @format */
'use client';
import LoadingSpinner from '@/components/ui/client/LoadingSpinner';
import Button from '@atoms/Button';
import Link from '@atoms/Link';
import { useWindowDimensions } from '@common/application/hooks/useWindowDimensions';
import { ResponseBody } from '@common/types/api';
import { ElasticPageHistory } from '@common/types/elastic';
import { css } from '@emotion/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import SortableTh from '@molecules/Table/SortableTh';
import clsx from 'clsx';
import { ScrollView } from 'devextreme-react';
import React, { ReactElement, useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Column, ColumnInstance, Row, useColumnOrder, useExpanded, useRowSelect, useSortBy, useTable } from 'react-table';
import { StyledTrFooter } from './styled';
import styles from './Table.module.css';

export interface SortableColumn<DataType extends object> extends ColumnInstance<DataType> {
  sortId?: string;
}

export interface TableProps<DataType extends object> {
  columns: SortableColumn<DataType>[] | ReadonlyArray<Column<DataType>>;
  // columns: Column<{}>[];
  data?: ResponseBody<DataType[]> | undefined;
  // data: ElasticResponseBody<DataType> | undefined;
  previousPage?: () => void;
  nextPage?: (data) => void;
  pageHistory?: ElasticPageHistory;
  loading?: boolean;
  className?: string;
  unpaginated?: boolean;
  onClickRow?: (row: Row<DataType>) => void;
  href?: (row: Row<DataType>) => string;
  // These are intended to be the state and dispatch returned from useTableSorting custom hook
  onSort?(sort: Record<string, 'asc' | 'desc'> | null): void;

  sort?: Record<string, 'asc' | 'desc'>;

  defaultSort?: { columnId: string; ascending: boolean };

  disableSorting?: boolean;

  renderRowSubComponent?(row: Row<DataType>): ReactElement;

  allowRenderToCard?: boolean;
}

interface PaginationDataProps {
  historyLength: number;
  size: number;
  total: number;
}

const PaginationInfo = (props: PaginationDataProps) => {
  const start = props.historyLength * props.size;
  const end = start + props.size;
  return (
    <span>
      {start} - {end < props.total ? end : props.total} of {props.total}
    </span>
  );
};

const size = 10;

export function Pagination<DataType>(props: { data: ResponseBody<DataType[]> | undefined; pageHistory?: ElasticPageHistory; previousPage: () => void; nextPage: (data) => void }) {
  return (
    <div className={styles.pagination}>
      {props.data ? (
        <>
          <PaginationInfo historyLength={props.pageHistory?.length ?? 0} size={size} total={props.data.total?.value} />
          <Button
            css={css`
              width: 28px;
              justify-content: center;
            `}
            onClick={props.previousPage}
            disabled={props.pageHistory?.length === 0}
          >
            <FontAwesomeIcon icon={['fas', 'angle-left']} />
          </Button>
          <Button
            css={css`
              width: 28px;
              justify-content: center;
            `}
            onClick={props.nextPage}
            disabled={(props.pageHistory?.length ?? 0) * size + (props.data?.payload ?? ([] as Array<unknown>)).length === props.data?.total?.value}
          >
            <FontAwesomeIcon icon={['fas', 'angle-right']} />
          </Button>
        </>
      ) : (
        <FormattedMessage defaultMessage="loading…" description="Loading text for tables" />
      )}
    </div>
  );
}

export function Table<DataType extends object>(props: TableProps<DataType>) {
  const initialState = { pageIndex: 0, sortBy: props.defaultSort ? [{ id: props.defaultSort.columnId, desc: !props.defaultSort.ascending }] : [] };
  const [hasMounted, setHasMounted] = useState(false);
  const { onSort } = props;
  // Memoize data to prevent infinite re-renders on error?
  const data = useMemo(() => {
    if (!Array.isArray(props.data?.payload)) return [];
    // react-table expects each row to be a defined object.
    return props.data.payload.filter((row): row is DataType => Boolean(row));
  }, [props.data?.payload]);
  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow, visibleColumns, columns, setSortBy } = useTable<DataType>(
    {
      columns: props.columns ?? [],
      data: data ?? [],
      // @ts-ignore
      initialState,
      manualPagination: true,
      pageCount: (props.data?.total?.value ?? 0) / size,
      autoResetExpanded: false,
      manualSortBy: !!props.onSort,
      disableSortBy: !!props.disableSorting,
      disableMultiSort: true,
    },
    useSortBy,
    useExpanded,
    useRowSelect,
    useColumnOrder,
    // () => {},
  );
  const _props = props;

  useEffect(() => {
    setHasMounted(true);
  }, []);

  const clearColumnSorting = () => {
    setSortBy([]);
  };
  const { width } = useWindowDimensions();
  const isCardView = props.allowRenderToCard && hasMounted && width < 1000;

  //Find the column that has sorting applied
  // Cast as SortableColumn to allow sortId property
  const sortedCol = columns.find((column) => column.isSorted) as SortableColumn<DataType>;

  const sortedColId = sortedCol?.id;
  const sortedColSortId = sortedCol?.sortId;
  const isSortedDesc = sortedCol?.isSortedDesc;

  // There has to be a better way to do this, but I can't find one
  useEffect(() => {
    if (!onSort) return;
    //pass the sorted column's info to the onSort function, so it can be passed to the query in parent component
    onSort(sortedColId ? { [sortedColSortId ?? sortedColId ?? 'createdAt']: isSortedDesc ? 'desc' : 'asc' } : null);
  }, [onSort, sortedColId, sortedColSortId, isSortedDesc]);

  const setSortToDefault = () => {
    if (!props.defaultSort) return;
    const sortCol = columns.find((col) => col.id === props.defaultSort?.columnId) as SortableColumn<DataType>;
    if (!sortCol?.sortId) return;
    props.onSort?.({ [sortCol.sortId]: props.defaultSort.ascending ? 'asc' : 'desc' });
  };

  const sortColumn = (column: SortableColumn<DataType>) => {
    if (!props.sort || !props.onSort) return;
    let sortObject;
    if (column.sortId && !props.sort.hasOwnProperty(column.sortId)) {
      sortObject = { [column.sortId]: 'asc' };
    } else if (column.sortId && props.sort[column.sortId] === 'asc') {
      sortObject = { [column.sortId]: 'desc' };
    } else {
      return setSortToDefault();
    }
    props.onSort(sortObject);
  };

  useEffect(() => {
    setSortToDefault();
  }, [props.defaultSort]);

  const TableElement = useMemo(
    () => (
      <table className={styles.table} {...getTableProps()}>
        <thead className={styles.thead}>
          {
            // Loop over the header rows
            headerGroups.map((headerGroup) => {
              const headerGroupProps = headerGroup.getHeaderGroupProps();
              return (
                // Apply the header row props
                <tr className={styles.tr} {...headerGroupProps} key={headerGroupProps.key}>
                  {
                    // Loop over the headers in each row
                    headerGroup.headers.map((column) => {
                      const columnHeaderProps = column.getHeaderProps(column.getSortByToggleProps());
                      return (
                        // Apply the header cell props
                        <SortableTh
                          sort={props.sort!}
                          columnHeaderProps={columnHeaderProps}
                          column={column}
                          sortColumn={sortColumn}
                          onSort={props.onSort}
                          key={columnHeaderProps.key}
                        />
                      );
                    })
                  }
                </tr>
              );
            })
          }
        </thead>
        <tbody {...getTableBodyProps()}>
          {
            // Loop over the table rows
            rows.map((row) => {
              // Prepare the row for display
              prepareRow(row);
              const rowProps = row.getRowProps();
              return (
                // Apply the row props
                [
                  <tr className={styles.tr} style={{ cursor: props.onClickRow ? 'pointer' : 'default' }} onClick={() => props.onClickRow?.(row)} {...rowProps} key={rowProps.key}>
                    {row.cells.map((cell) => {
                      const cellProps = cell.getCellProps();
                      return (
                        <td className={styles.td} {...cellProps} key={cellProps.key}>
                          {props.href ? <Link href={props.href(row)}>{cell.render('Cell')}</Link> : cell.render('Cell')}
                        </td>
                      );
                    })}
                  </tr>,
                  (row as any as { isExpanded: boolean }).isExpanded ? (
                    <tr className={styles.tr} key={`${rowProps.key}-expand`}>
                      <td colSpan={visibleColumns.length}>
                        {/*
                          Inside it, call our renderRowSubComponent function. In reality,
                          you could pass whatever you want as props to
                          a component like this, including the entire
                          table instance. But for this example, we'll just
                          pass the row
                        */}
                        {_props.renderRowSubComponent?.(row)}
                      </td>
                    </tr>
                  ) : undefined,
                ]
              );
            })
          }
        </tbody>
      </table>
    ),
    [_props, getTableBodyProps, getTableProps, headerGroups, prepareRow, props, rows, visibleColumns.length],
  );
  //
  // if (window?.innerWidth < 1000) {
  //   return <CardTable {...props} />;
  // }

  return (
    <div className={clsx(props.className, styles.container)}>
      {props.loading && (
        <div className={styles.loading}>
          <LoadingSpinner />
        </div>
      )}
      <div style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
        <ScrollView style={{ height: '100%' }}>{TableElement}</ScrollView>
      </div>
      {/*<PerfectScrollbar>{isCardView ? <CardTable {...props} /> : TableElement}</PerfectScrollbar>*/}
      <StyledTrFooter>
        <div>
          {props.unpaginated || !props.previousPage || !props.nextPage || (props?.data?.total?.value ?? 0) < size ? null : (
            <Pagination<DataType> data={props.data} pageHistory={props.pageHistory} previousPage={props.previousPage} nextPage={props.nextPage} />
          )}
        </div>
      </StyledTrFooter>
    </div>
  );
}

export default Table;
